import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ProductUnit } from '@/api/suppliers'
import { UNITS } from '@/api/suppliers'
import { supplierPortalApi } from '@/api/supplierPortal'
import { AppShell } from '@/components/AppShell'
import { IconBox, IconTrash } from '@/components/icons'
import { Alert, Button, EmptyState, Field, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { DeliverySettingsForm } from '@/features/delivery/DeliveryWindow'
import { formatDate } from '@/lib/format'


/** Κατάλογος προϊόντων με μαζική εισαγωγή από αρχείο (§11, οθόνη 4). */
export default function SupplierCatalogPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const fileInput = useRef<HTMLInputElement>(null)
  const [adding, setAdding] = useState(false)
  // Οι ελλείψεις δηλώνονται μαζί, το πρωί: ένα αίτημα ανά είδος θα σήμαινε
  // και μία ειδοποίηση ανά είδος στον πελάτη.
  const [missing, setMissing] = useState<Set<string>>(new Set())
  const [until, setUntil] = useState(today())
  const [showDelivery, setShowDelivery] = useState(false)

  const products = useQuery({ queryKey: ['supplier-products'], queryFn: () => supplierPortalApi.products() })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['supplier-products'] })

  const remove = useMutation({
    mutationFn: supplierPortalApi.deleteProduct,
    onSuccess: () => void invalidate(),
  })
  const importFile = useMutation({
    mutationFn: supplierPortalApi.importProducts,
    onSuccess: () => void invalidate(),
  })

  const delivery = useQuery({
    queryKey: ['supplier-delivery'],
    queryFn: supplierPortalApi.delivery,
  })
  const saveDelivery = useMutation({
    mutationFn: supplierPortalApi.updateDelivery,
    onSuccess: () => {
      setShowDelivery(false)
      void queryClient.invalidateQueries({ queryKey: ['supplier-delivery'] })
    },
  })

  const declareMissing = useMutation({
    mutationFn: () => supplierPortalApi.setAvailabilityBulk([...missing], until),
    onSuccess: () => {
      setMissing(new Set())
      void invalidate()
    },
  })
  const restore = useMutation({
    mutationFn: (id: string) => supplierPortalApi.setAvailability(id, null),
    onSuccess: () => void invalidate(),
  })

  const toggleMissing = (id: string) =>
    setMissing((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <AppShell
      title={t('nav.catalog')}
      action={!adding ? { label: t('portal.addProduct'), onClick: () => setAdding(true) } : null}
    >
      {adding && <ProductForm onDone={() => setAdding(false)} />}

      {/* Ωράριο παραγγελιών: ό,τι ορίζεται εδώ γίνεται αντίστροφη μέτρηση
          στην οθόνη αποστολής κάθε πελάτη. */}
      {showDelivery ? (
        <div className="mb-4">
          <DeliverySettingsForm
            window={delivery.data ?? null}
            saving={saveDelivery.isPending}
            onSave={(settings) => saveDelivery.mutate(settings)}
          >
            {saveDelivery.isError && <Alert>{translateError(saveDelivery.error)}</Alert>}
            <Button variant="secondary" type="button" onClick={() => setShowDelivery(false)}>
              {t('common.cancel')}
            </Button>
          </DeliverySettingsForm>
        </div>
      ) : (
        <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
          <h2 className="mb-1 font-semibold text-gray-900">{t('delivery.title')}</h2>
          <p className="mb-3 text-sm text-gray-500">
            {delivery.data?.cutoffTime
              ? t('delivery.cutoffAt', { time: delivery.data.cutoffTime.slice(0, 5) })
              : t('delivery.notSet')}
          </p>
          <Button variant="secondary" onClick={() => setShowDelivery(true)}>
            {t('delivery.edit')}
          </Button>
        </section>
      )}

      {/* Ελλείψεις ημέρας: επιλογή πολλών και μία δήλωση. */}
      {missing.size > 0 && (
        <section className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h2 className="font-semibold text-amber-900">{t('availability.bulkTitle')}</h2>
          <p className="text-sm text-amber-800">{t('availability.bulkHint')}</p>

          <label className="flex flex-col gap-1 text-sm font-medium text-amber-900">
            {t('availability.untilLabel')}
            <input
              type="date"
              value={until}
              min={today()}
              onChange={(event) => setUntil(event.target.value)}
              className="rounded-xl border border-amber-300 bg-white px-3 py-2.5 text-gray-900"
            />
          </label>

          {declareMissing.isError && <Alert>{translateError(declareMissing.error)}</Alert>}
          <Button loading={declareMissing.isPending} onClick={() => declareMissing.mutate()}>
            {t('availability.selectedCount', { count: missing.size })}
          </Button>
        </section>
      )}

      {declareMissing.data && (
        <div className="mb-4">
          <Alert tone="success">
            {t('availability.notified', { count: declareMissing.data.notifiedBusinesses })}
          </Alert>
        </div>
      )}

      <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
        <h2 className="mb-1 font-semibold text-gray-900">{t('portal.import')}</h2>
        <p className="mb-3 text-sm text-gray-500">{t('portal.importHint')}</p>

        {importFile.isError && <Alert>{translateError(importFile.error)}</Alert>}
        {importFile.data && (
          <div className="mb-3">
            <Alert tone="success">
              {t('portal.importResult', {
                created: importFile.data.created,
                updated: importFile.data.updated,
                skipped: importFile.data.skipped,
              })}
            </Alert>
            {importFile.data.errors.length > 0 && (
              <ul className="mt-2 list-inside list-disc text-xs text-amber-700">
                {importFile.data.errors.slice(0, 10).map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <input
          ref={fileInput}
          type="file"
          accept=".csv,.xlsx,.xlsm,text/csv"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) importFile.mutate(file)
            event.target.value = ''
          }}
        />
        <Button
          variant="secondary"
          loading={importFile.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {t('portal.chooseFile')}
        </Button>
      </section>

      {products.isPending && <SkeletonList rows={4} />}
      {products.data?.length === 0 && (
        <EmptyState
          icon={<IconBox size={26} />}
          title={t('portal.noProducts')}
          description={t('portal.noProductsHint')}
        />
      )}

      <ul className="flex flex-col gap-2">
        {products.data?.map((product) => (
          <li
            key={product.id}
            className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-gray-900">{product.name}</p>
              <p className="text-xs text-gray-500">
                {product.sku && `${product.sku} · `}
                {t(`units.${product.unit}`)}
                {product.price && ` · ${Number(product.price).toFixed(2)} €`}
              </p>
              {!product.isAvailableToday && product.unavailableUntil && (
                <button
                  onClick={() => restore.mutate(product.id)}
                  className="mt-1 inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                >
                  {t('availability.until', { date: formatDate(product.unavailableUntil) })}
                  <span className="font-semibold">· {t('availability.restore')}</span>
                </button>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {product.isAvailableToday && (
                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={missing.has(product.id)}
                    onChange={() => toggleMissing(product.id)}
                    className="size-4 accent-amber-600"
                  />
                  {t('availability.markMissing')}
                </label>
              )}
              <button
                aria-label={t('common.delete')}
                onClick={() => remove.mutate(product.id)}
                className="rounded-lg px-3 py-2 text-gray-400 transition-colors hover:text-red-600"
              >
                <IconTrash size={18} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </AppShell>
  )
}

function ProductForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const [name, setName] = useState('')
  const [unit, setUnit] = useState<ProductUnit>('kg')
  const [sku, setSku] = useState('')
  const [price, setPrice] = useState('')

  const create = useMutation({
    mutationFn: () =>
      supplierPortalApi.createProduct({
        name: name.trim(),
        unit,
        sku: sku.trim() || undefined,
        price: price.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supplier-products'] })
      onDone()
    },
  })

  return (
    <form
      className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (name.trim()) create.mutate()
      }}
    >
      <h2 className="font-semibold text-gray-900">{t('portal.addProduct')}</h2>
      {create.isError && <Alert>{translateError(create.error)}</Alert>}

      <Field
        label={t('portal.productName')}
        name="productName"
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
      />

      <div className="flex gap-3">
        <div className="flex-1">
          <Field
            label={t('portal.sku')}
            name="sku"
            value={sku}
            onChange={(event) => setSku(event.target.value)}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="unit" className="text-sm font-medium text-gray-700">
            {t('items.fields.unit')}
          </label>
          <select
            id="unit"
            value={unit}
            onChange={(event) => setUnit(event.target.value as ProductUnit)}
            className="h-touch rounded-xl border border-gray-300 bg-white px-3 text-base text-gray-900 outline-none focus:border-brand-600"
          >
            {UNITS.map((value) => (
              <option key={value} value={value}>
                {t(`units.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Field
        label={t('portal.price')}
        name="price"
        type="number"
        step="0.01"
        min="0"
        inputMode="decimal"
        value={price}
        onChange={(event) => setPrice(event.target.value)}
      />

      <div className="flex gap-2">
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}
